import { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'
import FlashMobileStickyHeaderFilters from '../FlashMobileStickyHeaderFilters'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Sheet from 'components/utils/Sheet'
import FlashHotelRateOption from 'components/OfferPage/FlashHotel/SameRoom/FlashHotelRateOption'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CarouselContainer = styled.div`
  padding: ${rem(12)};
`

const CarouselItemContainer = styled(Sheet)`
  min-width: 75vw;
  width: 100%;
`

interface Props {
  room: App.PackageRoomType
  packageOptions: Array<App.OfferPackageOptionView>
  onClose?: () => void
  onBack?: () => void
}

function FlashHotelPackageSelectionModalWithCarousel(props: Props) {
  const {
    room,
    packageOptions,
    onClose,
    onBack,
  } = props

  const intl = useIntl()
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])

  const modalTitle = intl.formatMessage({
    id: 'qqlOD-O8y4W_Kyp8kGyb',
    defaultMessage: 'Select a package',
    description: 'Heading title for package selection modal on the offer detail page',
  })

  const availablePackagesText = intl.formatMessage(
    {
      id: 'bkbbhVqu1OAc0_Dd3vET',
      defaultMessage: '{count, plural, one {# package option} other {# package options}} available',
      description: 'Text showing number of package options available on the offer detail page',
    },
    {
      count: packageViews.length,
    },
  )

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <FlashMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="none">
          {packageViews.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="light-foreground-alt">{availablePackagesText}</BodyText>
            </OptionsContainer>
          )}

          <CarouselContainer>
            <CardCarousel
              snap="start"
              width={packageViews.length === 1 ? '100%' : 'auto'}
              gap={12}
            >
              {packageViews.map((view, index) => (
                <CarouselItemContainer
                  key={view.package.uniqueKey}
                  border="light-foreground"
                >
                  <FlashHotelRateOption
                    packageView={view}
                    packagePosition={packageViews.length > 1 ? index + 1 : undefined}
                  />
                </CarouselItemContainer>
              ))}
            </CardCarousel>
          </CarouselContainer>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  rooms: state.rooms,
  packageOptions: getDurationPackageOptions(state, appState),
}), FlashHotelPackageSelectionModalWithCarousel)
